import _ from 'lodash';

import * as statuses from 'app/entity/utils/statuses';
import ActionTypes from 'app/entity/ActionTypes';
import { formatSuccess } from 'app/entity/utils/entityUtils';
import {
  normalizeDeleteError,
  normalizeGetError,
  normalizePatchError,
  normalizePostError,
} from 'app/entity/utils/normalizeFetchError';

export const initializeCollections = collections => ({
  type: ActionTypes.INITIALIZE_COLLECTIONS,
  collections,
});
// Don't partially apply entity since there's no
// entity to partially apply.
initializeCollections.partiallyApplyEntity = () => false;

export const resetCollection = entityType => ({
  type: ActionTypes.RESET_COLLECTION,
  entityType,
});

export const resetCollectionEntities = entityType => ({
  type: ActionTypes.RESET_COLLECTION_ENTITIES,
  entityType,
});

export const resetEntityStatus = (entityType, id) => ({
  type: ActionTypes.RESET_ENTITY_STATUS,
  entityType,
  id,
});

export const createCollectionEntity = (entityType, data, state) => ({
  type: ActionTypes.CREATE_COLLECTION_ENTITY,
  entityType,
  data,
  state,
});

export const createCollectionUiEntity = (entityType, data, state) =>
  createCollectionEntity(entityType, data, {
    ...(state || {}),
    ...{ isPersisted: false, status: statuses.INITIAL },
  });

export const deleteCollectionEntities = (entityType, ids) => ({
  type: ActionTypes.DELETE_COLLECTION_ENTITIES,
  entityType,
  ids,
});

export const updateCollectionData = (entityType, data) => ({
  type: ActionTypes.UPDATE_COLLECTION_DATA,
  entityType,
  data,
});

export const updateCollectionDataItem = (entityType, key, value) =>
  updateCollectionData(entityType, { [key]: value });

export const updateCollectionState = (entityType, state) => ({
  type: ActionTypes.UPDATE_COLLECTION_STATE,
  entityType,
  state,
});

export const updateCollectionStateItem = (entityType, key, value) =>
  updateCollectionState(entityType, { [key]: value });

const fetchCollection = (status, entityType, messages) => ({
  entityType,
  messages: _.get(messages, 'start'),
  shouldSilenceErrors: true,
  status,
  type: ActionTypes.FETCH_COLLECTION,
});

const formatCollectionSuccess = (messages, collection) => ({
  collection,
  status: statuses.SUCCESS,
  messages: _.get(messages, 'success'),
});

const collectionLoadingPromises = {};

const hasCollectionLoadingPromise = entityType =>
  !!collectionLoadingPromises[entityType];

const addCollectionLoadingPromise = (entityType, promise) => {
  collectionLoadingPromises[entityType] = promise;
  const removeCollectionLoadingPromise = () => {
    collectionLoadingPromises[entityType] = null;
  };
  // Remove the promise from the cache whenever it finishes.
  // Not returning this one since it's a different promise than
  // the actual cached promise and wouldn't contain the data.
  collectionLoadingPromises[entityType]
    .then(removeCollectionLoadingPromise)
    .catch(error => {
      removeCollectionLoadingPromise();
      throw error;
    });
  // Return the stored promise since it contains the data that's resolved.
  // Note that the stored promise is returned instead of the promise
  // chain above so that we're always returning the exact same promise.
  return collectionLoadingPromises[entityType];
};

export const loadCollectionWithType = (
  actionType,
  entityType,
  load,
  shouldLoad,
  isLoading,
  messages
) => {
  return (dispatch, getState) => {
    const state = getState();
    // If it's already loading then return the loading promise.
    // This is important if multiple `loadCollection` calls are
    // made for an entity before the collection has finished loading;
    // they should all finish when it's done loading.
    if (isLoading(state) && hasCollectionLoadingPromise(entityType)) {
      return collectionLoadingPromises[entityType];
      // If the collection shouldn't load at all then we can `resolve`
      // immediately; it's likely already loaded.
    } else if (!shouldLoad(state)) {
      return Promise.resolve(null);
    }
    // Otherwise we definitely need to load it.
    const promise = dispatch({
      ...fetchCollection(statuses.LOADING, entityType, messages),
      formatError: _.partial(normalizeGetError, entityType, messages),
      formatSuccess: _.partial(formatCollectionSuccess, messages),
      promise: load(getState),
      type: actionType,
    });
    // And then we need to store that promise for any subsequent
    // calls to `loadCollection` while `isLoading`.
    addCollectionLoadingPromise(entityType, promise);
    return promise;
  };
};

export const loadCollection = _.partial(
  loadCollectionWithType,
  ActionTypes.FETCH_COLLECTION
);
loadCollection.partiallyApplyEntity = (self, { type, api, selectors }) =>
  _.partial(
    self,
    type,
    api.loadCollection,
    selectors.all.shouldLoad,
    selectors.all.isLoading
  );

// This is used to bypass the selectors.all.shouldLoad check, this is needed when doing the preload collection flow
export const forceLoadCollection = _.partial(
  loadCollectionWithType,
  ActionTypes.FETCH_COLLECTION
);
forceLoadCollection.partiallyApplyEntity = (self, { type, selectors, api }) =>
  _.partial(
    self,
    type,
    api.loadCollection,
    _.stubTrue,
    selectors.all.isLoading
  );

export const loadCollectionSilently = _.partial(
  loadCollectionWithType,
  ActionTypes.SILENT_FETCH_COLLECTION
);
loadCollectionSilently.partiallyApplyEntity = (self, { type, api }) =>
  _.partial(self, type, api.loadCollection, _.stubTrue, () => false); // Silently fetching the collection should be allowed at any time

// The wrapper function here is required for loadCollectionFromCache.partiallyApplyEntity to work correctly
export const loadCollectionFromCache = _.partial(
  loadCollectionWithType,
  ActionTypes.FETCH_COLLECTION_FROM_CACHE
);
loadCollectionFromCache.partiallyApplyEntity = (
  self,
  { type, api, selectors }
) =>
  _.partial(
    self,
    type,
    api.loadCollectionFromCache,
    selectors.all.shouldLoad,
    () => false
  );

export const preloadCollection = (
  entityType,
  loadFromCache,
  loadFromApi,
  loadSilentlyFromApi,
  shouldLoad,
  isEntityEmpty,
  messages
) => {
  return (dispatch, getState) => {
    return !shouldLoad(getState())
      ? Promise.resolve(null)
      : dispatch(loadFromCache(messages)).then(() => {
          const load = isEntityEmpty(getState())
            ? loadFromApi
            : loadSilentlyFromApi;
          return dispatch(load(messages));
        });
  };
};
preloadCollection.partiallyApplyEntity = (self, entityConfig) =>
  _.partial(
    self,
    entityConfig.type,
    loadCollectionFromCache.partiallyApplyEntity(
      loadCollectionFromCache,
      entityConfig
    ),
    forceLoadCollection.partiallyApplyEntity(forceLoadCollection, entityConfig),
    loadCollectionSilently.partiallyApplyEntity(
      loadCollectionSilently,
      entityConfig
    ),
    entityConfig.selectors.all.isInitialLoad,
    entityConfig.selectors.all.isEmpty
  );

export const saveCollection = (
  entityType,
  extractDataFromState,
  save,
  messages
) => {
  return (dispatch, getState) => {
    const data = extractDataFromState(getState());
    return dispatch({
      ...fetchCollection(statuses.SAVING, entityType, messages),
      formatError: _.partial(normalizePatchError, entityType, messages),
      formatSuccess: _.partial(formatCollectionSuccess, messages),
      promise: save(data, getState),
    });
  };
};
saveCollection.partiallyApplyEntity = (self, { type, api, selectors }) =>
  _.partial(self, type, selectors.all.entitiesData, api.saveCollection);

export const loadCollectionWithParams = (
  entityType,
  load,
  shouldLoad,
  isLoading,
  params,
  messages
) =>
  loadCollection(
    entityType,
    _.partial(load, params),
    shouldLoad,
    isLoading,
    messages
  );
loadCollectionWithParams.partiallyApplyEntity = (
  self,
  { type, api, selectors }
) =>
  _.partial(
    self,
    type,
    api.loadCollectionWithParams,
    selectors.all.shouldLoad,
    selectors.all.isLoading
  );

export const resetAndLoadCollection = (
  entityType,
  load,
  shouldLoad,
  isLoading,
  messages
) => {
  return dispatch => {
    dispatch(resetCollection(entityType));
    return dispatch(
      loadCollection(entityType, load, shouldLoad, isLoading, messages)
    );
  };
};
resetAndLoadCollection.partiallyApplyEntity = (
  self,
  { type, api, selectors }
) =>
  _.partial(
    self,
    type,
    api.loadCollection,
    selectors.all.shouldLoad,
    selectors.all.isLoading
  );

export const updateEntityData = (entityType, data, id) => ({
  type: ActionTypes.UPDATE_ENTITY_DATA,
  entityType,
  data,
  id,
});

export const updateEntityDataItem = (entityType, key, value, id) =>
  updateEntityData(entityType, { [key]: value }, id);

export const updateEntityState = (entityType, state, id) => ({
  type: ActionTypes.UPDATE_ENTITY_STATE,
  entityType,
  state,
  id,
});

export const updateEntityStateItem = (entityType, key, value, id) =>
  updateEntityState(entityType, { [key]: value }, id);

export const fetchEntity = (status, entityType, messages, id) => ({
  entityType,
  id,
  messages: _.get(messages, 'start'),
  notificationDuration: _.get(messages, 'notificationDuration', undefined),
  shouldGlobalizeError: _.get(messages, 'shouldGlobalizeError', false),
  shouldSilenceErrors: _.get(messages, 'shouldSilenceErrors', true),
  shouldNotify: _.get(messages, 'shouldNotify', false),
  status,
  type: ActionTypes.FETCH_ENTITY,
});

export const loadEntity = (entityType, load, messages, id) => {
  return (dispatch, getState) =>
    dispatch({
      ...fetchEntity(statuses.LOADING, entityType, messages, id),
      formatError: _.partial(normalizeGetError, entityType, messages),
      formatSuccess: _.partial(formatSuccess, messages),
      promise: load(id, getState),
    });
};
loadEntity.partiallyApplyEntity = (self, { type, api }) =>
  _.partial(self, type, api.load);

export const loadEntityWithParams = (entityType, load, params, messages, id) =>
  loadEntity(entityType, _.partial(load, params), messages, id);
loadEntityWithParams.partiallyApplyEntity = (self, { type, api }) =>
  _.partial(self, type, api.loadWithParams);

const getEntityData = (selectors, id, state) => {
  const entity = selectors.all.entity(id, state);
  return selectors.data(entity);
};

export const saveEntity = (
  entityType,
  extractDataFromState,
  save,
  messages,
  id
) => {
  return (dispatch, getState) => {
    const data = extractDataFromState(id, getState());
    return dispatch({
      ...fetchEntity(statuses.SAVING, entityType, messages, id),
      formatError: _.partial(normalizePatchError, entityType, messages),
      formatSuccess: _.partial(formatSuccess, messages),
      promise: save(id, data, getState),
    });
  };
};
saveEntity.partiallyApplyEntity = (self, { type, selectors, api }) =>
  _.partial(self, type, _.partial(getEntityData, selectors), api.save);

export const saveEntityWithParams = (
  entityType,
  extractDataFromState,
  save,
  params,
  messages,
  id
) =>
  saveEntity(
    entityType,
    extractDataFromState,
    _.partial(save, params),
    messages,
    id
  );
saveEntityWithParams.partiallyApplyEntity = (self, { type, selectors, api }) =>
  _.partial(
    self,
    type,
    _.partial(getEntityData, selectors),
    api.saveWithParams
  );

export const deleteEntity = (
  entityType,
  extractDataFromState,
  deleteFn,
  messages,
  id
) => {
  return (dispatch, getState) => {
    const data = extractDataFromState(id, getState());
    return dispatch({
      ...fetchEntity(statuses.SAVING, entityType, messages, id),
      formatError: _.partial(normalizeDeleteError, entityType, messages),
      formatSuccess: _.partial(formatSuccess, messages),
      // The reducer expects `null` when an entity is deleted, so assume
      // that `deleteFn` returns entity data from the server and return `null`
      // to ensure that that entity data doesn't make it into the reducer
      // from this action, otherwise the entity won't be removed from the store.
      promise: deleteFn(id, data, getState).then(() => null),
    });
  };
};
deleteEntity.partiallyApplyEntity = (self, { type, api }) =>
  // By default deleting an entity sends no data, so
  // return `undefined`.
  _.partial(self, type, () => undefined, api.delete);

export const createEntity = (
  entityType,
  extractDataFromState,
  create,
  messages,
  tempId
) => {
  return (dispatch, getState) => {
    const data = extractDataFromState(tempId, getState());
    return dispatch({
      ...fetchEntity(statuses.SAVING, entityType, messages, tempId),
      formatError: _.partial(normalizePostError, entityType, messages),
      formatSuccess: _.partial(formatSuccess, messages),
      promise: create(_.omit(data, 'id'), getState),
    });
  };
};
createEntity.partiallyApplyEntity = (self, { type, selectors, api }) =>
  _.partial(self, type, _.partial(getEntityData, selectors), api.create);

export const createEntityWithParams = (
  entityType,
  extractDataFromState,
  create,
  params,
  messages,
  id
) =>
  createEntity(
    entityType,
    extractDataFromState,
    _.partial(create, params),
    messages,
    id
  );
createEntityWithParams.partiallyApplyEntity = (
  self,
  { type, selectors, api }
) =>
  _.partial(
    self,
    type,
    _.partial(getEntityData, selectors),
    api.createWithParams
  );

export const createAndSaveEntity = (
  entityType,
  create,
  messages,
  data,
  cid
) => dispatch => {
  const dataClone = {
    cid,
    id: cid,
    ...data,
  };
  const provideData = () => data;
  // Create entity in the local store state.
  dispatch(createCollectionUiEntity(entityType, dataClone, {}));
  // Kick off an API request to send the entity data to the server.
  return dispatch(
    createEntity(entityType, provideData, create, messages, dataClone.id)
  );
};
createAndSaveEntity.partiallyApplyEntity = (self, { type, api }) =>
  _.partial(self, type, api.create);
